import { Directive, ElementRef, Input, NgZone, OnChanges, OnDestroy } from '@angular/core';
import { Provider } from '@voly/ui-components/src/v2/providers/chakra';
import { ComponentType, createElement } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { AngularBridgeContext, AngularServices } from 'react-lib/context/AngularBridgeContext';
import { system as pinpointSystem } from 'react-lib/utils/PinpointTheme';
import { wrapCallbacksInZone } from './react-wrapper-directive.utils';

@Directive({
  standalone: true,
  selector: '[reactComponent]',
})
export class ReactWrapperDirective<TProps extends object = Record<string, unknown>>
  implements OnChanges, OnDestroy
{
  @Input() reactComponent: ComponentType<any>;
  @Input() props: TProps = {} as TProps;
  @Input() services?: AngularServices = {};

  private root: Root | null = null;

  constructor(
    private elm: ElementRef,
    private zone: NgZone,
  ) {}

  ngOnChanges(): void {
    // it passes the new props into React's reconciliation (diffing) algorithm. React then determines what, if
    // anything, actually needs to update in the DOM.

    // It is functionally equivalent to a parent React component re-rendering with new props for a child. The React
    // component instance is preserved, its internal state is preserved, and only the parts of the DOM that depend on
    // changed props are updated.
    this.render();
  }

  private render(): void {
    if (!this.reactComponent) {
      return;
    }

    const enrichedProps = wrapCallbacksInZone(this.props, (callback) => this.zone.run(callback));

    if (!this.root) {
      this.root = createRoot(this.elm.nativeElement);
    }

    this.root.render(
      createElement(Provider, {
        system: pinpointSystem,
        forcedTheme: 'light',
        enableColorScheme: false,
        children: createElement(AngularBridgeContext.Provider, {
          value: this.services,
          children: createElement(this.reactComponent, enrichedProps),
        }),
      }),
    );
  }

  ngOnDestroy(): void {
    this.root?.unmount();
  }
}
